import { useMemo, useState } from 'react';
import {
  SearchField, Spinner, Alert, Pagination,
} from '@openedx/paragon';
import { FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';

import { useLiveSessions } from '../../gql/hooks/useLiveSessions';
import LiveSessionCard from './LiveSessionCard';
import emptyLiveSVG from '../../assets/empty-live.svg';
import messages from './messages';
import './LiveSessions.scss';

const PAGE_SIZE = 12;

const LiveSessions = () => {
  const { formatMessage } = useIntl();
  const { sessions, loading, error } = useLiveSessions(100, ['scheduled', 'live', 'ended']);
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (!searchValue) return sessions;
    const q = searchValue.toLowerCase();
    return sessions.filter((s) => (
      s.title?.toLowerCase().includes(q)
      || s.subtitle?.toLowerCase().includes(q)
      || s.description?.toLowerCase().includes(q)
    ));
  }, [sessions, searchValue]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="live-sessions-page">
      <div className="live-sessions-page__top">
        <h1 className="live-sessions-page__title">
          {formatMessage(messages.heading)}
        </h1>
        <SearchField
          onSubmit={setSearchValue}
          onClear={() => setSearchValue('')}
          onChange={setSearchValue}
          value={searchValue}
          placeholder={formatMessage(messages.searchPlaceholder)}
          screenReaderText={{
            label: formatMessage(messages.searchLabel),
            submitButton: formatMessage(messages.searchSubmit),
            clearButton: formatMessage(messages.searchClear),
          }}
        />
      </div>

      {loading && (
        <div className="live-sessions-page__state">
          <Spinner
            animation="border"
            screenReaderText={formatMessage(messages.loading)}
          />
        </div>
      )}

      {!loading && error && (
        <Alert variant="danger">
          <FormattedMessage {...messages.loadError} />
        </Alert>
      )}

      {!loading && !error && sessions.length === 0 && (
        <div className="live-sessions-page__empty-state">
          <img
            src={emptyLiveSVG}
            alt={formatMessage(messages.emptyStateImageAlt)}
            className="live-sessions-page__empty-state-image"
          />
          <div className="live-sessions-page__empty-state-text">
            <h2 className="live-sessions-page__empty-state-title">
              {formatMessage(messages.emptyStateTitle)}
            </h2>
            <p className="live-sessions-page__empty-state-subtitle">
              {formatMessage(messages.emptyStateSubtitle)}
            </p>
          </div>
        </div>
      )}

      {!loading && !error && sessions.length > 0 && filtered.length === 0 && (
        <div className="live-sessions-page__state">
          <p className="live-sessions-page__empty">
            <FormattedMessage {...messages.noSessionsFound} />
          </p>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <>
          <div className="live-sessions-page__grid">
            {paginated.map((s) => (
              <LiveSessionCard
                key={s.id}
                id={s.id}
                title={s.title}
                imageUrl={s.cardImageUrl}
                status={s.status}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="live-sessions-page__pagination">
              <Pagination
                paginationLabel={formatMessage(messages.paginationLabel)}
                pageCount={totalPages}
                currentPage={page}
                onPageSelect={setPage}
                variant="reduced"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LiveSessions;
